import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, X } from 'lucide-react';

interface RejectSheetProps {
  onCancel: () => void;
  /** Sends the job back. The reason is already trimmed and known non-empty. */
  onConfirm: (reason: string) => Promise<void>;
}

/**
 * Sending completed work back to the floor (decision D7).
 *
 * The server has supported this transition since the state machine was written and the
 * client had no way to fire it — a verifier who found a fault half-fixed could only
 * close it or leave it sitting. This is that missing door.
 *
 * A reason is mandatory here, not merely encouraged. The server enforces it too, but a
 * round trip that comes back 400 tells the verifier nothing and costs them the typing;
 * the submit stays disabled until there is something to send. Whitespace does not count,
 * because " " satisfies a required field and answers no question — the technician
 * reading this on a phone in a plant room needs to know what was wrong with the fix.
 *
 * A textarea rather than `window.prompt`, which is what Hold still uses: prompt gives no
 * room to write, no validation, and on Android renders a system dialogue that looks like
 * the browser has intervened rather than the app asking a question.
 */
export function RejectSheet({ onCancel, onConfirm }: RejectSheetProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmed = reason.trim();
  const ready = trimmed.length > 0;

  async function confirm(): Promise<void> {
    if (!ready) return;

    setBusy(true);
    setError(null);
    try {
      await onConfirm(trimmed);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('reject.failed'));
      setBusy(false);
    }
  }

  // Portalled for the same reason ProofSheet is: the action toolbar is `position:
  // sticky`, which creates a stacking context unconditionally, so a sheet rendered
  // inside it sits beneath the fixed tab bar however high its z-index goes.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end bg-black/70">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('reject.heading')}
        className="w-full rounded-t-2xl border-t border-slate-800 bg-slate-950 p-4 pb-[max(1.5rem,var(--safe-bottom))] pl-[max(1rem,var(--safe-left))] pr-[max(1rem,var(--safe-right))]"
      >
        <header className="mb-3 flex items-center justify-between">
          <p className="text-base font-semibold text-slate-100">{t('reject.heading')}</p>
          <button
            type="button"
            onClick={onCancel}
            aria-label={t('reject.cancel')}
            className="flex size-12 min-h-0 items-center justify-center bg-transparent p-0 text-slate-400"
          >
            <X className="size-5" aria-hidden="true" />
          </button>
        </header>

        <p className="mb-3 text-sm text-slate-400">{t('reject.explain')}</p>

        <textarea
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          rows={4}
          aria-label={t('reject.reasonLabel')}
          placeholder={t('reject.placeholder')}
          className="mb-2 w-full rounded-xl border border-slate-700 bg-slate-900 p-3 text-base text-slate-100 placeholder:text-slate-500"
        />

        {/* Says why the button is dead rather than leaving the verifier to guess. */}
        {!ready && <p className="mb-2 text-xs text-amber-400">{t('reject.reasonRequired')}</p>}

        {error && (
          <p role="alert" className="mb-2 text-sm text-red-400">
            {error}
          </p>
        )}

        <button
          type="button"
          disabled={!ready || busy}
          onClick={() => void confirm()}
          className="flex min-h-[56px] w-full items-center justify-center gap-2 rounded-xl bg-amber-600 px-4 text-base font-semibold text-white disabled:bg-amber-950 disabled:text-amber-300"
        >
          {busy && <Loader2 className="size-5 animate-spin" aria-hidden="true" />}
          {t('reject.confirm')}
        </button>
      </div>
    </div>,
    document.body,
  );
}
